import { ChangeDetectionStrategy, Component, ChangeDetectorRef, OnInit, HostListener } from '@angular/core';
import { UrlBuilder } from '../../../core';
import { FormControl, Validators } from '@angular/forms';
import { maxLengthInBytes, maxBytesError, dateValidation, futureDateValidation } from '../../../shared/validators';
import { RegisterSampleStorage } from '../register-sample-storage';
import { AccessionSearchResult, accessionDetailsHeader, ErrorMsg, VisitInfo, AccessionData } from './../register-sample-info';
import { AccessionDetailsService } from '../service/accession-details-service';
import { FormDropdown } from '../../../sample-tracking/sample-monitoring/interface/notification-data';
import { uniqBy } from 'lodash';
import * as moment from 'moment';
import { ActivityLogger } from '../../../activities/activity-logger';
import { OverrideVisit } from './override-visit/override-visit-info';
import { IMdlCustomDialogConfiguration, MdlDialogService } from '@angular-mdl/core';
import { OverrideVisitComponent, OVERRIDE_VISIT_TOKEN } from './override-visit/override-visit.component';
import { switchMap, map } from 'rxjs/operators';

@Component({
    selector: 'ereq-accession-details',
    template: require('./accession-details.component.html'),
    styles: [require('./accession-details.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EreqAccessionDetailsComponent {

    public showBarcodeScanner = false;
    public showBarcodeLoading = false;
    public accessionFormControl: FormControl = new FormControl('', [Validators.required, Validators.pattern('[0-9]+'), maxLengthInBytes(10)]);
    public subjectId: FormControl = new FormControl('', maxLengthInBytes(100));
    public visit: FormControl = new FormControl('', maxLengthInBytes(100));
    public visitDate: FormControl = new FormControl('', [Validators.pattern('[0-9]{2}-[a-zA-z]{3}-[0-9]{4}$'), dateValidation(), futureDateValidation()]);
    public collectionTime: FormControl = new FormControl('', [Validators.pattern('^([01][0-9]|2[0-3]):([0-5][0-9])$')]);
    public visitDropDown: FormDropdown[] = [];
    public visitInfoList: VisitInfo[] = [];
    public showVisitLoading = false;
    public patienError = '';
    public errorText = '';
    public warningText = '';
    public accessionData: AccessionSearchResult;
    public accessionDetails: any;
    public showOtherDetails = false;
    public currentDateTime: moment.Moment;
    public timeError = '';
    public selectedVisit: VisitInfo;
    public isSampleDeleted: boolean;
    public disableOverrideVisit: boolean;
    public zoomLevel = 1;

    constructor(
        private urlBuilder: UrlBuilder,
        private registerSampleStorage: RegisterSampleStorage,
        private accessionService: AccessionDetailsService,
        private changeDetectorRef: ChangeDetectorRef,
        private dialogService: MdlDialogService,
        private activityLogger: ActivityLogger) {

        activityLogger.logActivity('Registered Samples - Sample Entry');
    }

    public ngOnInit() {
        this.initializeData();
        this.checkPatientIds();
        this.updateVisitDetails();
        this.validateCollectionDate();
        this.calculateZoomLevel();
    }

    @HostListener('window:resize', ['$event'])
    public onResize(event: any) {
        this.calculateZoomLevel();
    }

    public initializeData() {
        let data = localStorage.getItem('accessionDetails');
        if (!data) {
            this.urlBuilder.esa().sampleManagement().registeredSamples().navigate();
        }
        let accessionDetails = JSON.parse(data);
        this.addAccession(accessionDetails);
    }

    public addAccession(accessionDetails?: any) {
        if (accessionDetails) {
            this.accessionFormControl.setValue(accessionDetails.sampleRegistrationAccessionData[0].accessionNumber);
        }
        let value = this.accessionFormControl.value;
        this.clearData();
        this.warningText = '';
        if (accessionDetails) {
            this.validateAccession(accessionDetails, value);
            return;
        }
        this.registerSampleStorage.getAccessionDetails(value).subscribe((result: AccessionSearchResult) => {
            this.validateAccession(result, value);
        });
    }

    public openBarcodeScanner() {
        this.showBarcodeScanner = !this.showBarcodeScanner;
    }

    public barcodeEvent(event: any) {
        this.showBarcodeScanner = false;
        this.accessionFormControl.setValue(event.barcodeData);
        if (!event.barcodeData) { return; }
        this.addAccession();
    }

    public cancel() {
        this.urlBuilder.esa().sampleManagement().pendingRegistration().navigate();
    }

    public nextClick() {
        if (!this.showOtherDetails) {
            this.addAccession();
            return;
        }
        let data = this.accessionService.getSaveRequestData(this.accessionData.sampleRegistrationAccessionData[0], this.selectedVisit);
        this.registerSampleStorage.saveVisitDetails(data).subscribe(() => {
            localStorage.setItem('resumeRegistration', this.accessionData.sampleRegistrationAccessionData[0].accessionNumber);
            this.urlBuilder.esa().sampleManagement().resumeRegistration().navigate();
        });
    }

    public disableAction() {
        if (!this.showOtherDetails && !this.errorText && !this.errorText.length && this.accessionFormControl.value) {
            return false;
        }
        return !this.showOtherDetails || this.patienError.length || this.visitDate.invalid || !this.subjectId.value || this.timeError.length ||
            !this.visitDate.value || this.collectionTime.invalid || !this.collectionTime.value || (this.visitDropDown.length > 1 && !this.visit.value);
    }

    public onVisitSelected($event: FormDropdown) {
        if (!$event) {
            this.updateHeaderDetails('', 'protocolVisitCode');
            this.selectedVisit = null;
            return;
        }
        this.activityLogger.logActivity('Registered Samples - Visit Entry');
        this.selectedVisit = $event.data;
        this.updateHeaderDetails(this.selectedVisit.protocolVisitCode, 'protocolVisitCode');
    }

    public async onOverrideVisitClick($event: { key: string, control: FormControl }) {
        this.showOverrideVisitDialog();
    }

    private loadVisitDetails(accessionData: AccessionData[]) {
        if (!accessionData || !accessionData.length) { return; }
        this.showVisitLoading = true;
        this.registerSampleStorage.getVisitDetails(accessionData[0]).subscribe((result: VisitInfo[]) => {
            if (result && result.length) {
                let dropDownData = uniqBy(result.map(vd => { return { key: vd.visitScheduleId, value: vd.protocolVisitDescription, data: vd }; }), 'key');
                if (dropDownData.length === 1) {
                    this.selectedVisit = dropDownData[0].data;
                    this.visit.setValue(dropDownData[0].value);
                    this.visitDropDown = [];
                    this.updateHeaderDetails(dropDownData[0].data.protocolVisitCode, 'protocolVisitCode');
                } else {
                    this.visitDropDown = dropDownData;
                }
            }
            this.showVisitLoading = false;
            this.visitInfoList = result;
            this.changeDetectorRef.markForCheck();
        });
    }

    private checkAccessionNumber() {
        this.accessionFormControl.valueChanges.subscribe((value: string) => {
            if (this.accessionFormControl.hasError('pattern')) {
                this.errorText = ErrorMsg.OnlyNumberError;
            } else if (this.accessionFormControl.hasError(maxBytesError) || (value && value.length !== 10)) {
                this.errorText = ErrorMsg.MaxBytesError;
            } else {
                this.errorText = '';
            }
        });
    }

    private updateVisitDetails() {
        this.visit.valueChanges.subscribe((value: string) => {
            this.updateHeaderDetails(value, 'protocolVisitDescription');
        });
    }

    private checkPatientIds() {
        this.subjectId.valueChanges.subscribe((value: string) => {
            this.patienError = this.accessionService.validatePatientId(value);
            if (!this.patienError.length && this.accessionData && this.accessionData.sampleRegistrationAccessionData) {
                this.updateHeaderDetails(value, 'patientIdentifier');
            }
        });
    }

    private clearData() {
        this.showBarcodeLoading = true;
        this.showOtherDetails = false;
        this.subjectId.setValue('');
        this.visit.setValue('');
        this.collectionTime.setValue('');
        this.visitDate.setValue('');
        this.changeDetectorRef.markForCheck();
    }

    private validateCollectionDate() {
        this.visitDate.valueChanges.subscribe((value: string) => {
            if (value && value.length && this.visitDate.valid) {
                this.validateCollectionDateTime();
                this.updateHeaderDetails(value, 'visitCollectionDateTime');

            }
        });
        this.collectionTime.valueChanges.subscribe((value: string) => {
            this.timeError = '';
            if (value && value.length && this.collectionTime.valid) {
                this.validateCollectionDateTime();
            }
        });
    }

    private validateCollectionDateTime() {
        this.timeError = '';
        if (!this.collectionTime.value) { return; }
        if (this.collectionTime.value === '00:00') {
            this.timeError = 'Please enter valid Collection Time';
            return;
        }
        if (!this.visitDate.value) { return; }
        let time = new Date();
        let isVisitDateValid = this.accessionService.checkCollectionDateTime(moment(this.visitDate.value).format('YYYY-MM-DD'));
        let addedTime = this.collectionTime.value.split(':');
        if (!isVisitDateValid && this.visitDate.value && (+addedTime[0] > time.getHours() ||
            (+addedTime[0] === time.getHours() && +addedTime[1] > time.getMinutes()))) {
            this.timeError = 'You entered a future time. Samples can only be registered once they have been collected. Please review your entry.';
        } else {
            this.updateHeaderDetails(this.collectionTime.value, 'visitCollectionTime');
        }
    }

    private updateHeaderDetails(value: any, header: string) {
        if (!this.accessionData || !this.accessionData.sampleRegistrationAccessionData) { return; }
        let data: any = this.accessionData.sampleRegistrationAccessionData[0];
        data[header as keyof AccessionData] = value;
    }

    //delete sample registration code
    private deleteSampleRegistrationDialog(accessionNo: string, registedVisitId: number) {
        this.accessionService.showDeleteWarning(
            {
                message: `Sample registration has been submitted for this accession: <br> <br> 
                <b>${accessionNo} </b><br> <br>Do you want to delete existing registration and start over?`,
                reverseButtons: true,
                yesButtonText: 'Delete Registration',
                noButtonText: 'Cancel',
                title: 'Samples'
            },
            true).subscribe((res: any) => {
                if (res !== false) {
                    if (res.isYesButtonClicked) {
                        this.OpenDeleteSampleRegistration(accessionNo, registedVisitId, res.isYesButtonClicked);
                    }
                }
            });
    }

    private async OpenDeleteSampleRegistration(accessionNo: string, registedVisitId: number, isYesButtonClicked: boolean) {
        let result = await this.accessionService.deleteRegistrationReasonDialog(accessionNo, isYesButtonClicked).toPromise();
        if (result.isCancelled) { return; }
        let deleteReason = result.selectedOption.confirmOptions;
        let request = {
            registeredVisitId: registedVisitId,
            deleteReason: deleteReason,
            deletedOn: `${moment().local().format('YYYY-MM-DD hh:mm:ss Z')}`
        };
        await this.registerSampleStorage.deleteSampleRegistration(request).subscribe(() => {
            this.isSampleDeleted = true;
            this.errorText = '';
            this.changeDetectorRef.markForCheck();
            setTimeout(() => { this.isSampleDeleted = false; this.changeDetectorRef.markForCheck(); }, 10000);
        });
    }

    private showOverrideVisitDialog() {
        let requestInfo: OverrideVisit = {
            accessionData: this.accessionData.sampleRegistrationAccessionData[0]
        };
        return this.showAdditionalDialog(requestInfo)
            .pipe(switchMap(dialog => dialog.onHide()), map(dialogResult => dialogResult)).subscribe((result: VisitInfo) => {
                if (result) {
                    this.selectedVisit = result;
                    this.visit.setValue(result.protocolVisitDescription);
                    this.updateHeaderDetails(result.protocolVisitCode, 'protocolVisitCode');
                }
            });
    }

    private showAdditionalDialog(dialogRequest: OverrideVisit) {

        const configDialog: IMdlCustomDialogConfiguration = {
            component: OverrideVisitComponent,
            providers: [{
                provide: OVERRIDE_VISIT_TOKEN, useValue: dialogRequest
            }],
            isModal: true,
            classes: 'sr-dialog',
            clickOutsideToClose: false
        };

        return this.dialogService.showCustomDialog(configDialog);
    }

    private calculateZoomLevel() {
        const devicePixelRatio = window.devicePixelRatio || 1;
        this.zoomLevel = Math.round((window.outerWidth / window.innerWidth) * 100) / (devicePixelRatio > 1 ? devicePixelRatio : 1);
    }

    private showKitRevisionWarning(data: AccessionData): void {
        if (data?.kitTypeRevisionIdBuild !== data?.kitTypeRevisionIdCurrent) {
            this.accessionService.showWarning(`/${data.projectNumber}/${data.investigatorNumber}/documents/site-communications`, ErrorMsg.KitUpdated);
        }
    }

    private validateAccession(result: AccessionSearchResult, value: string) {
        if (result.errorMsg.toLowerCase() === ErrorMsg.SubmittedSamples.toLowerCase()) {
            this.errorText = ErrorMsg.SubmittedSamples;
            result.isError = true;
            this.deleteSampleRegistrationDialog(value, result.sampleRegistrationAccessionData[0].registeredVisitId);
        } else {
            this.showKitRevisionWarning(result.sampleRegistrationAccessionData[0]);
            this.errorText = '';
            this.showOtherDetails = true;
            this.loadVisitDetails(result.sampleRegistrationAccessionData);
            let data = result.sampleRegistrationAccessionData ? result.sampleRegistrationAccessionData[0] : null;
            this.disableOverrideVisit = data && data.kitType.startsWith('U') ? true : false;
            this.accessionDetails = { header: accessionDetailsHeader, data: data };
        }
        this.accessionData = result;
        this.showBarcodeLoading = false;
        this.changeDetectorRef.markForCheck();
    }
}
